import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartItem, Product } from '../types';
import { CUSTOM_BASE_PRODUCTS, DESIGN_FEE_VECTORIAL, DESIGN_FEE_FOTO_DIRECTA } from '../constants';

interface ConfigEntry {
  tempId: string;
  productId: string;
  size: string;
  color: string;
  quantity: number;
}

interface CustomDesignProps {
  onAddToCart: (item: CartItem) => void;
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
  // o instantáneo: window.scrollTo(0, 0);
};

const CustomDesign: React.FC<CustomDesignProps> = ({ onAddToCart }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [configEntries, setConfigEntries] = useState<ConfigEntry[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [designType, setDesignType] = useState<'premium' | 'economico'>('premium');
  const [designImageUrl, setDesignImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const totalPrice = useMemo(() => {
    const itemsPrice = configEntries.reduce((sum, entry) => {
      const p = CUSTOM_BASE_PRODUCTS.find(p => p.id === entry.productId);
      return sum + ((p?.price || 0) * entry.quantity);
    }, 0);

    const feePrice = designType === 'premium' 
      ? DESIGN_FEE_VECTORIAL.price 
      : DESIGN_FEE_FOTO_DIRECTA.price;

    return itemsPrice + feePrice;
  }, [configEntries, designType]);

  const uploadImage = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "sanan_custom");

      const res = await fetch(
        "https://api.cloudinary.com/v1_1/dwbp8tmlv/image/upload",
        { method: "POST", body: formData }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error?.message || "Error al subir la imagen");
      }

      const data = await res.json();
      setDesignImageUrl(data.secure_url);
    } catch (err: any) {
      console.error("Error subida Cloudinary:", err);
      setUploadError(err.message || "No se pudo subir la imagen. Intenta otra.");
      setDesignImageUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const addProductConfig = (productId: string) => {
    const product = CUSTOM_BASE_PRODUCTS.find(p => p.id === productId);
    if (!product) return;

    const newEntry: ConfigEntry = {
      tempId: Math.random().toString(36).substring(2, 9),
      productId,
      size: product.sizes[0] || 'Único',
      color: product.colors[0] || '#000000',
      quantity: 1,
    };
    setConfigEntries(prev => [...prev, newEntry]);
  };

  const removeConfigEntry = (tempId: string) => {
    setConfigEntries(prev => prev.filter(e => e.tempId !== tempId));
  };

  const updateEntry = (tempId: string, updates: Partial<ConfigEntry>) => {
    setConfigEntries(prev =>
      prev.map(e => (e.tempId === tempId ? { ...e, ...updates } : e))
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!selected.type.startsWith('image/')) {
      alert("Solo se permiten imágenes");
      return;
    }
    if (selected.size > 10 * 1024 * 1024) {
      alert("La imagen no puede superar 10 MB");
      return;
    }

    setFile(selected);
    setDesignImageUrl(null);     // reset previo
    setUploadError(null);
    uploadImage(selected);
  };

  const handleFinish = () => {
    if (configEntries.length === 0) {
      alert("Añade al menos un producto");
      return;
    }

    if (!file) {
      alert("Selecciona una imagen de tu máquina");
      return;
    }

    if (uploading) {
      alert("La imagen aún se está subiendo, espera un momento...");
      return;
    }

    if (!designImageUrl) {
      alert("Hubo un problema al subir la imagen. Por favor intenta subirla de nuevo.");
      return;
    }

    setIsAdding(true);

    const feeProduct = designType === 'premium' 
      ? DESIGN_FEE_VECTORIAL 
      : DESIGN_FEE_FOTO_DIRECTA;

    onAddToCart({
      product: feeProduct,
      quantity: 1,
      size: 'Único',
      color: 'Original',
      customImageUrl: designImageUrl,
      designType,
    });

    configEntries.forEach(entry => {
      const product = CUSTOM_BASE_PRODUCTS.find(p => p.id === entry.productId);
      if (product) {
        onAddToCart({
          product,
          quantity: entry.quantity,
          size: entry.size,
          color: entry.color,
          customImageUrl: designImageUrl,
        });
      }
    });

    setTimeout(() => {
      navigate('/checkout');
    }, 1200);
  };

  const getCheckColor = (hex: string): string => {
    if (!hex) return 'text-white';

    const color = hex.toLowerCase().trim();

    if (
      color === '#ffffff' ||
      color === 'white' ||
      color === '#f5f5f5' ||
      color === '#f6f6f8' ||
      color === '#fafafa' ||
      color === '#eeeeee' ||
      color === '#f8f8f8'
    ) {
      return 'text-black';
    }

    let r = 0, g = 0, b = 0;
    if (hex.startsWith('#') && hex.length === 7) {
      r = parseInt(hex.slice(1, 3), 16);
      g = parseInt(hex.slice(3, 5), 16);
      b = parseInt(hex.slice(5, 7), 16);
    }

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? 'text-black' : 'text-white';
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* HEADER DE SECCIÓN */}
      <section className="bg-black text-white py-16 px-6 md:px-10 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[url('/heading_person.png')] bg-cover bg-center"></div>
        <div className="relative z-10">
          <span className="text-primary font-bold uppercase tracking-[0.4em] text-[10px] mb-4 block">SANAN Lab</span>
          <h1 className="text-5xl md:text-7xl font-black uppercase italic tracking-tighter leading-none mb-4">
            Tu Máquina, <span className="text-primary">Tu Prenda</span>
          </h1>
          <p className="text-gray-400 text-sm md:text-lg max-w-2xl mx-auto font-medium italic">
            Transformamos la belleza de tu máquina en piezas de colección exclusivas.
          </p>
        </div>
      </section>

      {/* PROGRESS BAR */}
      <div className="w-full h-1 bg-gray-100 sticky top-16 z-40">
        <div 
          className="h-full bg-primary transition-all duration-700" 
          style={{ width: `${(step / 3) * 100}%` }}
        ></div>
      </div>

      <div className="flex-1 max-w-[1400px] mx-auto w-full p-6 md:p-12 lg:p-20">
        
        {step === 1 && (
          <div className="space-y-16 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
              <div className="flex flex-col items-center text-center gap-6 group">
                <div className="size-24 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100 group-hover:border-primary transition-all relative">
                  <span className="text-4xl font-black italic text-black/10 absolute -top-2 -left-2">01</span>
                  <span className="material-symbols-outlined text-4xl text-primary">check_box</span>
                </div>
                <div>
                  <h3 className="font-black uppercase italic text-lg mb-2">Elige Productos</h3>
                  <p className="text-gray-500 text-xs leading-relaxed italic">
                    Selecciona los artículos que quieres personalizar: sudaderas, camisetas, tazas, botellas...
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center text-center gap-6 group">
                <div className="size-24 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100 group-hover:border-primary transition-all relative">
                  <span className="text-4xl font-black italic text-black/10 absolute -top-2 -left-2">02</span>
                  <span className="material-symbols-outlined text-4xl text-primary">photo_camera</span>
                </div>
                <div>
                  <h3 className="font-black uppercase italic text-lg mb-2">Sube tu Máquina</h3>
                  <p className="text-gray-500 text-xs leading-relaxed italic">
                    Envíanos una foto clara. Nuestros diseñadores vectorizarán cada detalle.
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center text-center gap-6 group">
                <div className="size-24 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100 group-hover:border-primary transition-all relative">
                  <span className="text-4xl font-black italic text-black/10 absolute -top-2 -left-2">03</span>
                  <span className="material-symbols-outlined text-4xl text-primary">fact_check</span>
                </div>
                <div>
                  <h3 className="font-black uppercase italic text-lg mb-2">Verifica el Diseño</h3>
                  <p className="text-gray-500 text-xs leading-relaxed italic">
                    Te enviamos un render final para que lo apruebes antes de entrar en producción.
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center text-center gap-6 group">
                <div className="size-24 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100 group-hover:border-primary transition-all relative">
                  <span className="text-4xl font-black italic text-black/10 absolute -top-2 -left-2">04</span>
                  <span className="material-symbols-outlined text-4xl text-primary">local_shipping</span>
                </div>
                <div>
                  <h3 className="font-black uppercase italic text-lg mb-2">Arte en tu Casa</h3>
                  <p className="text-gray-500 text-xs leading-relaxed italic">
                    Producimos tu pedido y lo recibes cómodamente en casa.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-3xl p-8 md:p-12 border border-gray-100 shadow-sm">
              <h2 className="text-3xl font-black uppercase italic tracking-tighter mb-8 text-center md:text-left">
                Elige tu Tipo de Diseño
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div 
                  onClick={() => setDesignType('premium')}
                  className={`group relative rounded-2xl overflow-hidden border-2 transition-all cursor-pointer p-6 flex flex-col h-full ${
                    designType === 'premium' ? 'border-primary shadow-xl scale-[1.02]' : 'border-gray-200 hover:border-primary/50 hover:shadow-lg'
                  }`}
                >
                  <div className="absolute top-4 right-4 z-10">
                    {designType === 'premium' && (
                      <span className="bg-primary text-white text-xs font-black px-3 py-1 rounded-full">Seleccionado</span>
                    )}
                  </div>
                  <div className="aspect-video rounded-xl overflow-hidden mb-6 grayscale group-hover:grayscale-0 transition-all">
                    <img 
                      src="/vector_original.png"
                      alt="Diseño vectorial premium"
                      className="w-full h-full object-cover object-[center_20%]"
                    />
                  </div>
                  <h3 className="font-black uppercase italic text-xl mb-3">Diseño Vectorial Premium</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    Recreamos tu vehículo desde cero a partir de tu foto: proporciones ajustadas, líneas limpias y estilo minimalista optimizado para impresión. 
                    Resultado más impactante, uniforme y exclusivo.
                  </p>
                  <div className="mt-auto flex items-center gap-4">
                    <span className="text-3xl font-black italic">29.90€</span>
                    <span className="text-[10px] font-black uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 rounded-full">
                      Opción Profesional
                    </span>
                  </div>
                </div>

                <div 
                  onClick={() => setDesignType('economico')}
                  className={`group relative rounded-2xl overflow-hidden border-2 transition-all cursor-pointer p-6 flex flex-col h-full ${
                    designType === 'economico' ? 'border-primary shadow-xl scale-[1.02]' : 'border-gray-200 hover:border-primary/50 hover:shadow-lg'
                  }`}
                >
                  <div className="absolute top-4 right-4 z-10">
                    {designType === 'economico' && (
                      <span className="bg-primary text-white text-xs font-black px-3 py-1 rounded-full">Seleccionado</span>
                    )}
                  </div>
                  <div className="aspect-video rounded-xl overflow-hidden mb-6 grayscale group-hover:grayscale-0 transition-all">
                    <img 
                      src="\vector_foto_n_.png"
                      alt="Vectorización de Fotografía"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-black uppercase italic text-xl mb-3">Vectorización de Fotografía</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    Convertimos tu foto en vector manteniendo exactamente la posición y forma original del vehículo. 
                    Ideal si buscas una opción económica sin modificar el diseño original.
                  </p>
                  <div className="mt-auto flex items-center gap-4">
                    <span className="text-3xl font-black italic">13.90€</span>
                    <span className="text-[10px] font-black uppercase tracking-widest text-green-600 bg-green-50 px-3 py-1 rounded-full">
                      Más Económica
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-center text-gray-500 text-sm italic mt-8 max-w-3xl mx-auto">
                La tasa es única por modelo de coche. En pedidos futuros del mismo vehículo, no se cobra de nuevo.
              </p>


<div className="mt-8 max-w-3xl mx-auto px-6 py-5 bg-gray-50/70 border border-blue-900/50 rounded-2xl shadow-sm">
  <p className="text-center text-gray-700 text-sm font-medium italic leading-relaxed tracking-wide">
    Los diseños mantienen exactamente el mismo estilo que los de la tienda, pero con <span className="font-semibold text-gray-900 not-italic">tu coche</span>.<br className="hidden sm:block" />
    En prendas: logo <span className="font-semibold text-gray-900">SANAN</span> delante y el coche en la espalda.  
    En accesorios: logo <span className="font-semibold text-gray-900">SANAN</span> pequeño y tu coche como protagonista absoluto.
  </p>
</div>

            </div>


           

            <button 
              onClick={() => {
    setStep(2);
    scrollToTop();
  }}
              className="w-full bg-black text-white h-20 rounded-2xl font-black uppercase tracking-[0.2em] italic hover:bg-neutral-800 transition-all shadow-2xl flex items-center justify-center gap-4"
            >
              Iniciar Configuración
              <span className="material-symbols-outlined">arrow_forward</span>
            </button>
          </div>
        )}

{step === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 animate-fade-in">

            {/* 1. Elige tu Base - primer bloque en móvil */}
            <div className="lg:col-span-5 space-y-8 order-1 lg:order-none">
              <header>
                <h2 className="text-3xl font-black uppercase italic tracking-tighter mb-2">Elige tu Base</h2>
                <p className="text-gray-400 text-sm italic">Haz clic para añadir un artículo a tu configuración.</p>
              </header>
              
              <div className="grid grid-cols-1 gap-4">
                {CUSTOM_BASE_PRODUCTS.map(product => (
                  <button 
                    key={product.id}
                    onClick={() => addProductConfig(product.id)}
                    className="group flex items-center gap-6 p-4 bg-white border border-gray-100 rounded-2xl hover:border-primary hover:shadow-xl transition-all text-left active:scale-95"
                  >
                    <div className="size-20 rounded-xl overflow-hidden grayscale group-hover:grayscale-0 transition-all shrink-0">
                      <img src={product.image} className="w-full h-full object-cover" alt={product.name} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-black uppercase italic text-sm mb-1">{product.name}</h4>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{product.price.toFixed(2)}€</p>
                    </div>
                    <span className="material-symbols-outlined text-primary opacity-0 group-hover:opacity-100 transition-opacity">add_circle</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 2. Mi Laboratorio - segundo bloque en móvil */}
            <div className="lg:col-span-7 space-y-8 order-2 lg:order-none">
              <header className="flex justify-between items-end border-b border-gray-100 pb-6">
                <div>
                  <h2 className="text-3xl font-black uppercase italic tracking-tighter">Mi Laboratorio</h2>
                  <p className="text-gray-400 text-sm italic">Gestiona las tallas, colores y cantidades de cada prenda.</p>
                </div>
                <span className="text-xs font-black italic bg-primary/10 text-primary px-4 py-1.5 rounded-full">
                  {configEntries.length} Artículo{configEntries.length !== 1 ? 's' : ''}
                </span>
              </header>

              {configEntries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200 opacity-50">
                  <span className="material-symbols-outlined text-5xl mb-4">biotech</span>
                  <p className="font-black uppercase italic text-sm tracking-tighter">El laboratorio está vacío</p>
                  <p className="text-[10px] text-gray-400 font-bold uppercase mt-1">Añade productos del catálogo para empezar</p>
                </div>
              ) : (
                <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                  {configEntries.map(entry => {
                    const p = CUSTOM_BASE_PRODUCTS.find(x => x.id === entry.productId);
                    if (!p) return null;
                    return (
                      <div key={entry.tempId} className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
                        <div className="flex flex-wrap gap-6 items-start">
                          <div className="size-24 rounded-2xl overflow-hidden bg-gray-50 border border-gray-100 shrink-0">
                            <img src={p.image} className="w-full h-full object-cover" alt={p.name} />
                          </div>

                          <div className="flex-1 min-w-[200px] space-y-5">
                            <div className="flex justify-between items-start">
                              <h3 className="font-black uppercase italic text-lg">{p.name}</h3>
                              <button 
                                onClick={() => removeConfigEntry(entry.tempId)} 
                                className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"
                              >
                                <span className="material-symbols-outlined text-xl">delete</span>
                              </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                              <div className="space-y-2">
                                <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 italic">Cantidad</label>
                                <div className="flex items-center bg-gray-50 rounded-lg overflow-hidden border border-gray-100 h-10 w-fit">
                                  <button 
                                    onClick={() => updateEntry(entry.tempId, { quantity: Math.max(1, entry.quantity - 1) })} 
                                    className="px-4 hover:bg-gray-200 text-gray-600 transition-colors"
                                  >-</button>
                                  <span className="w-12 text-center text-sm font-black">{entry.quantity}</span>
                                  <button 
                                    onClick={() => updateEntry(entry.tempId, { quantity: entry.quantity + 1 })} 
                                    className="px-4 hover:bg-gray-200 text-gray-600 transition-colors"
                                  >+</button>
                                </div>
                              </div>

                              <div className="space-y-2">
                                <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 italic">Talla</label>
                                <div className="flex gap-1 flex-wrap">
                                  {p.sizes.map(s => (
                                    <button 
                                      key={s}
                                      onClick={() => updateEntry(entry.tempId, { size: s })}
                                      className={`size-8 text-[9px] font-black rounded border flex items-center justify-center transition-all ${
                                        entry.size === s ? 'bg-black text-white border-black' : 'bg-white text-gray-400 border-gray-100 hover:border-gray-300'
                                      }`}
                                    >{s}</button>
                                  ))}
                                </div>
                              </div>

                              {p.colors.length > 0 && (
                                <div className="space-y-2">
                                  <label className="text-[9px] font-black uppercase tracking-widest text-gray-400 italic">Color</label>
                                  <div className="flex flex-wrap gap-2.5">
                                    {p.colors.map(c => {
                                      const isSelected = entry.color === c;
                                      return (
                                        <button
                                          key={c}
                                          onClick={() => updateEntry(entry.tempId, { color: c })}
                                          className={`relative size-8 rounded-full border-2 transition-all duration-200 flex items-center justify-center transform hover:scale-110 active:scale-95 ${
                                            isSelected 
                                              ? 'border-primary scale-105 shadow-md shadow-primary/30 ring-1 ring-primary/40' 
                                              : 'border-transparent hover:border-gray-300'
                                          }`}
                                          style={{ backgroundColor: c }}
                                          title={c}
                                        >
                                          {isSelected && (
                                            <span className={`material-symbols-outlined text-base font-black ${getCheckColor(c)}`}>
                                              check
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 3. Presupuesto + botones - último en móvil */}
            <div className="lg:col-span-5 order-3 lg:order-none lg:mt-0 mt-10">
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 italic">Presupuesto Estimado</span>
                  <span className="text-2xl font-black italic">{totalPrice.toFixed(2)}€</span>
                </div>

                <button 
                  disabled={configEntries.length === 0}
                  onClick={() => {
                    setStep(3);
                    scrollToTop();
                  }}
                  className={`w-full h-14 rounded-xl font-black uppercase tracking-widest italic flex items-center justify-center gap-2 transition-all shadow-md ${
                    configEntries.length > 0 
                      ? 'bg-black text-white hover:bg-neutral-800' 
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Siguiente Paso
                  <span className="material-symbols-outlined">arrow_forward</span>
                </button>

                <button
                  onClick={() => {
                    setStep(1);
                    scrollToTop();
                  }}
                  className="w-full h-12 rounded-xl font-black uppercase tracking-widest italic text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined">arrow_back</span>
                  Atrás
                </button>
              </div>
            </div>

          </div>
        )}

        {step === 3 && (
          <div className="space-y-12 animate-fade-in max-w-4xl mx-auto">
            <header className="text-center space-y-4 mb-16">
              <h2 className="text-4xl font-black uppercase italic tracking-tighter">Sube Tu Mejor Imagen</h2>
              <p className="text-gray-400 font-medium italic">Envíanos el material de referencia para que el equipo de diseño empiece a trabajar.</p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="space-y-8">
                <h3 className="text-xl font-black uppercase italic border-b border-gray-100 pb-4">Instrucciones de Imagen</h3>
                <div className="space-y-6">
                  <div className="flex gap-4 items-start">
                    <div className="size-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-sm">light_mode</span>
                    </div>
                    <div>
                      <h4 className="font-black uppercase italic text-xs mb-1">Iluminación Natural</h4>
                      <p className="text-[10px] text-gray-500 font-medium italic">Fotos en exterior con buena luz para ver todos los detalles.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="size-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-sm">photo_camera_front</span>
                    </div>
                    <div>
                      <h4 className="font-black uppercase italic text-xs mb-1">Ángulos Claros</h4>
                      <p className="text-[10px] text-gray-500 font-medium italic">Perfil lateral o frontal ¾ son los mejores para una ilustración perfecta.</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-start">
                    <div className="size-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-sm">block</span>
                    </div>
                    <div>
                      <h4 className="font-black uppercase italic text-xs mb-1">Sin Obstrucciones</h4>
                      <p className="text-[10px] text-gray-500 font-medium italic">Evita reflejos excesivos o elementos que tapen partes del vehículo.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4 italic">Tu Configuración Lab</h4>
                  <ul className="space-y-2 mb-6">
                    <li className="flex justify-between text-xs italic">
                      <span className="text-gray-500 font-medium">
                        {designType === 'premium' 
                          ? 'Tasa de Diseño Vectorial Premium' 
                          : 'Tasa de Vectorización de Fotografía'}
                      </span>
                      <span className="font-black">
                        {designType === 'premium' ? '29.90€' : '13.90€'}
                      </span>
                    </li>
                    {configEntries.map(entry => {
                      const p = CUSTOM_BASE_PRODUCTS.find(p => p.id === entry.productId);
                      return (
                        <li key={entry.tempId} className="flex justify-between text-xs italic">
                          <span className="text-gray-500 font-medium">
                            {p?.name} {entry.quantity > 1 ? `(x${entry.quantity})` : ''}
                          </span>
                          <span className="font-black">{((p?.price || 0) * entry.quantity).toFixed(2)}€</span>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="flex justify-between items-center pt-4 border-t border-gray-50">
                    <span className="text-lg font-black italic uppercase tracking-tighter">Total</span>
                    <span className="text-2xl font-black italic">{totalPrice.toFixed(2)}€</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-6">
                <div className={`relative border-2 border-dashed rounded-3xl p-10 flex flex-col items-center justify-center text-center transition-all min-h-[320px] ${
                  designImageUrl ? 'border-green-500 bg-green-50/30' 
                  : uploading ? 'border-yellow-500 bg-yellow-50/30 animate-pulse' 
                  : uploadError ? 'border-red-500 bg-red-50/30' 
                  : file ? 'border-primary bg-primary/5' 
                  : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                }`}>
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleFileChange}
                    className="absolute inset-0 opacity-0 cursor-pointer" 
                    disabled={uploading}
                  />

                  {uploading ? (
                    <div className="text-primary font-medium text-lg animate-pulse">Subiendo imagen... por favor espera</div>
                  ) : uploadError ? (
                    <div className="text-red-600 font-medium text-center max-w-[90%] space-y-3">
                      <p>{uploadError}</p>
                      <button 
                        onClick={() => { setUploadError(null); setFile(null); }}
                        className="px-5 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition"
                      >
                        Intentar de nuevo
                      </button>
                    </div>
                  ) : designImageUrl ? (
                    <div className="w-full space-y-5">
                      <img 
                        src={designImageUrl} 
                        alt="Vista previa de tu máquina" 
                        className="max-h-64 mx-auto rounded-xl shadow-lg object-contain border border-gray-200"
                      />
                      <p className="text-sm font-medium truncate max-w-[280px] mx-auto">{file?.name}</p>
                      <button 
                        onClick={() => { setFile(null); setDesignImageUrl(null); setUploadError(null); }}
                        className="text-red-600 text-sm font-black uppercase hover:underline"
                      >
                        Cambiar imagen
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <span className="material-symbols-outlined text-6xl text-gray-400">cloud_upload</span>
                      <div>
                        <p className="font-black text-base italic tracking-tight">Arrastra tu foto aquí o haz clic</p>
                        <p className="text-sm text-gray-500 mt-1">JPG, PNG • máx. 10 MB</p>
                      </div>
                      <span className="inline-block bg-black text-white px-6 py-3 rounded-xl text-sm font-black uppercase tracking-wider">
                        Seleccionar archivo
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-4">
                  <button 
                    disabled={!file || uploading || !designImageUrl || isAdding || configEntries.length === 0}
                    onClick={handleFinish}
                    className={`h-20 w-full rounded-2xl font-black uppercase tracking-[0.2em] italic transition-all shadow-2xl flex items-center justify-center gap-4 ${
                      !file || uploading || !designImageUrl || isAdding || configEntries.length === 0
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-primary text-white hover:bg-blue-700 active:scale-95'
                    }`}
                  >
                    {isAdding 
                      ? 'Sincronizando...' 
                      : uploading 
                        ? 'Subiendo imagen...' 
                        : 'Finalizar y Pagar'}
                    <span className="material-symbols-outlined">shopping_cart_checkout</span>
                  </button>

                  <button
                    onClick={() => {
    setStep(2);
    scrollToTop();
  }}
                    className="group mt-6 w-full sm:w-auto mx-auto flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-black uppercase text-base tracking-widest text-primary bg-white border-2 border-primary/30 hover:bg-primary/10 hover:border-primary hover:shadow-md transition-all duration-300 active:scale-95"
                  >
                    <span className="material-symbols-outlined text-2xl transition-transform group-hover:-translate-x-1">
                      arrow_back
                    </span>
                    Modificar selección
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomDesign;